import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import { parse, addDays, addMonths, endOfMonth, isBefore, add, startOfMonth, differenceInCalendarDays } from 'date-fns';
import { updateDatesFilter } from '../../mainFilterSlice';
import { scrollToResults } from '../../../../utilities/helpers';
import translate from '../../../../utilities/translator';
import { pushEvent } from '../../../../utilities/analytics';
import { WHEN_FILTER_UPDATE_EVENT } from '../../../../constants/analyticEvents';
import { filterClearClick, setLastChangedFilter } from '../../../../appSlice';
import useHandleMainFilterState from '../../../../hooks/mainFilter/useHandleMainFilterState';
import format from '../../../../utilities/format';
import Months from './Months';
import DatesTutorial from '../../../listTutorial/DatesTutorial';
import FilterItem from '../../FilterItem';
import Switch from '../../../../components/Switch';
import { selectDateFilter, selectDatesLabel, selectMonthAhead } from '../../selectors';
import { selectSearch, updateSearch } from '../../searchSlice';
import DateNights from './DateNights';
import TypeSwitcher from './TypeSwitcher';
import useSearchPerformed from '../../../../hooks/useSearchPerformed';
import abTesting from '../../../../services/abTesting';

function Dates({ handleShowFade, addFilterToSet }) {
  const clearTimerRef = useRef(null);
  const saveTimerRef = useRef(null);
  const dispatch = useDispatch();
  const filterState = useSelector(selectDateFilter);
  const monthAhead = useSelector(selectMonthAhead) || 12;
  const search = useSelector(selectSearch);
  const { flexible } = filterState.initial;
  const { nights: renderNights } = filterState;
  const [isClean, setIsClean] = useState(false);
  const label = useSelector(selectDatesLabel);
  const [isOpen, setIsOpen] = useState(false);
  const { minNights, maxNights } = filterState;
  const [type, setType] = useState(search.when?.type || 'month');
  const [months, setMonths] = useState(filterState.months || []);
  const [startDate, setStartDate] = useState(
    filterState.startDate ? parse(filterState.startDate, 'dd/MM/yyyy', new Date()) : null
  );
  const [endDate, setEndDate] = useState(
    filterState.endDate ? parse(filterState.endDate, 'dd/MM/yyyy', new Date()) : null
  );
  const [weekendsOnly, setWeekendsOnly] = useState(filterState.weekendsOnly || false);
  const [weekendsDisabled, setWeekendsDisabled] = useState(false);
  const [nights, setNights] = useState(filterState.initial.nightsList);
  const showClearButton =
    (search.when.type && search.when.type !== 'month') ||
    search.when.months?.length > 0 ||
    search.when.nights?.length > 0 ||
    search.when.weekendsOnly;
  const showDatesTutorial = window.innerHeight > 720 && type === 'month' && !abTesting.hideDateTutorial;

  const datePickerEndDate = useMemo(() => addMonths(new Date(), monthAhead), [monthAhead]);
  const endDateWithLastDay = endOfMonth(datePickerEndDate);

  let daysInterval = 20;
  if (type === 'range' && startDate && endDate) {
    daysInterval = differenceInCalendarDays(endDate, startDate);
  }

  const [setAppTimeout] = useSearchPerformed();

  // update filter after wizard changes.
  useEffect(() => {
    setType(filterState.type);
    setMonths(filterState.months || []);
    setWeekendsOnly(filterState.weekendsOnly);
    setStartDate(filterState.startDate ? parse(filterState.startDate, 'dd/MM/yyyy', new Date()) : null);
    setEndDate(filterState.endDate ? parse(filterState.endDate, 'dd/MM/yyyy', new Date()) : null);
  }, [filterState]);


  useEffect(() => {
    return () => {
      clearTimeout(clearTimerRef.current);
      clearTimeout(saveTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (type === 'range' && startDate && endDate) {
      setNights(
        nights.map((night) => ({ ...night, isSelected: daysInterval < night.value ? false : night.isSelected }))
      );
    }
  }, [type, startDate, endDate]);

  useEffect(() => {
    if (Object.keys(search.when).length === 0) {
      const clean = !!startDate && !!endDate && months.length === 0;
      setIsClean(clean);
    }

    const datesEqual = (date1, date2) => {
      if (date1 instanceof Date) {
        date1 = format(date1, 'dd/MM/yyyy');
      }

      if (date2 instanceof Date) {
        date2 = format(date1, 'dd/MM/yyyy');
      }

      if (type === 'month') return true;

      if (!date1 && !date2) return true;

      return date1 === date2;
    };

    const isFilterClean = () => {
      if (type !== filterState.type) {
        return false;
      }

      if (
        type === 'month' &&
        !!weekendsOnly === !!filterState.weekendsOnly &&
        (JSON.stringify(months) === JSON.stringify(filterState.months) ||
          (months.length === 0 && !filterState.months)) &&
        JSON.stringify(nights.filter(({ isSelected }) => isSelected).map(({ value }) => value)) ===
          JSON.stringify(filterState.nights)
      ) {
        return true;
      }

      if (
        type !== 'month' &&
        datesEqual(startDate, filterState.startDate) &&
        datesEqual(endDate, filterState.endDate) &&
        !!weekendsOnly === !!filterState.weekendsOnly
      ) {
        if (type === 'range') {
          return (
            JSON.stringify(nights.filter(({ isSelected }) => isSelected).map(({ value }) => value)) ===
            JSON.stringify(filterState.nights)
          );
        }

        return true;
      }

      return false;
    };

    setIsClean(isFilterClean());
  }, [search.when, startDate, endDate, months, type, filterState, weekendsOnly, nights]);

  useEffect(() => {
    if (
      isOpen &&
      (type === 'specific' || type === 'range') &&
      startDate &&
      window.innerWidth < 768 &&
      document.querySelector('.filter-popup__body')
    ) {
      document.querySelector('.filter-popup__body').scrollTop =
        document.querySelector('.filter-popup__body').scrollTop +
        document
          .querySelector('.react-datepicker__day--selected')
          .parentElement.parentElement.parentElement.getBoundingClientRect().top -
        138;
    }
  }, [startDate, type, isOpen]);

  useEffect(() => {
    if (!renderNights) return undefined;
    setNights((nights) =>
      nights.map((night) => ({
        ...night,
        isSelected: renderNights.includes(night.value),
      }))
    );
  }, [renderNights]);

  useEffect(() => {
    const activeNights = nights.filter(({ isSelected }) => isSelected).map(({ value }) => value);

    if (isFinite(Math.max(...activeNights)) && Math.max(...activeNights) >= 5) {
      setWeekendsOnly(false);
      setWeekendsDisabled(true);
    } else {
      setWeekendsDisabled(false);
      // setWeekendsOnly(!!filterState.weekendsOnly);
    }
  }, [nights, filterState.weekendsOnly]);

  const [isOpenPopup, setIsOpenPopup] = useHandleMainFilterState({
    filter: 'when',
  });

  const onDateChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);

    // Make warmup call only if user select full range
    if (end) {
      makeWarmupCall({
        startDate: format(start, 'dd/MM/yyyy'),
        endDate: format(end, 'dd/MM/yyyy'),
      });
    }
  };

  const formatDatesForState = () => {
    let dates;

    switch (type) {
      case 'month':
        dates = {
          type,
          months,
          minNights: minNights !== 0 ? minNights : undefined,
          maxNights: maxNights !== 0 ? maxNights : undefined,
          nights: nights.filter(({ isSelected }) => isSelected).map(({ value }) => value) || [],
          weekendsOnly,
        };
        break;

      case 'specific':
        dates = {
          type,
          startDate,
          endDate,
        };
        break;

      case 'range':
        dates = {
          type,
          startDate,
          endDate,
          weekendsOnly,
          minNights: minNights !== 0 ? minNights : undefined,
          maxNights: maxNights !== 0 ? maxNights : undefined,
          nights: nights.filter(({ isSelected }) => isSelected).map(({ value }) => value) || [],
        };
        break;

      default:
        dates = {
          type,
          startDate,
          endDate,
        };
        break;
    }

    if (dates.startDate instanceof Date) {
      dates.startDate = format(startDate, 'dd/MM/yyyy');
    }

    if (dates.endDate instanceof Date) {
      dates.endDate = format(endDate, 'dd/MM/yyyy');
    }

    return dates;
  };

  const handleSaveClick = () => {
    if (isClean) return undefined;

    const dates = formatDatesForState();
    setIsOpenPopup(false);
    saveTimerRef.current = setTimeout(() => {
      dispatch(setLastChangedFilter('when'));
      dispatch(updateDatesFilter(dates));
      dispatch(updateSearch({ when: dates }));
    }, 0);
    handleShowFade();
    scrollToResults();
    pushEvent(WHEN_FILTER_UPDATE_EVENT, dates);
    setAppTimeout(7000);
  };

  window.external_set_date = (dates) => {
    addFilterToSet(() => {
      setIsOpenPopup(false);
      saveTimerRef.current = setTimeout(() => {
        dispatch(setLastChangedFilter('when'));
        dispatch(updateDatesFilter(dates));
        dispatch(updateSearch({ when: dates }));
      }, 0);
      handleShowFade();
      scrollToResults();
      pushEvent(WHEN_FILTER_UPDATE_EVENT, dates);
      setAppTimeout(7000);
    });
  };

  const handleClearClick = () => {
    if (isClean && !startDate && !endDate && months.length === 0 && minNights === 0 && maxNights === 0) {
      return undefined;
    }

    dispatch(filterClearClick());

    clearTimerRef.current = setTimeout(() => {
      dispatch(
        updateDatesFilter({
          type: 'month',
          startDate: null,
          endDate: null,
          months: [],
          nights: [],
        })
      );
      dispatch(
        updateSearch({
          when: {
            type: 'month',
            months: [],
            nights: [],
          },
        })
      );
    }, 0);

    setType('month');
    setStartDate(null);
    setEndDate(null);
    setWeekendsOnly(false);
    setMonths([]);
    setNights(
      nights.map((night) => ({
        ...night,
        isSelected: false,
      }))
    );
    scrollToResults();
  };

  window.external_reset_date = () => {
    addFilterToSet(() => {
      handleClearClick();
    });
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const makeWarmupCall = (props) => {
    // warmupDealsCache({
    //   when: {
    //     ...formatDatesForState(),
    //     ...props
    //   }
    // })
  };

  const changeNights = (value) => () => {
    const updatedNights = nights.map((night) => ({
      ...night,
      isSelected: value === night.value ? !night.isSelected : night.isSelected,
    }));
    setNights(updatedNights);
    makeWarmupCall({ nights: updatedNights.filter(({ isSelected }) => isSelected).map(({ value }) => value) || [] });
  };

  const onMonthsChange = (months) => {
    setMonths(months);
    makeWarmupCall({ months });
  };

  const updateType = (value) => {
    setType(value);
    const updatedNights = nights.map((n) => ({ ...n, isSelected: false }));
    setNights(updatedNights);
    makeWarmupCall({
      type: value,
      nights: updatedNights,
    });
  };

  const onChangeWeekendsOnly = () => {
    setWeekendsOnly(!weekendsOnly);
    makeWarmupCall({
      weekendsOnly: !weekendsOnly,
    });
  };

  const onClickOutside = () => {
    setNights(
      nights.map((night) => ({
        ...night,
        isSelected: renderNights?.includes(night.value),
      }))
    );
  };

  const getLastShownMonth = () => {
    const DATE_FORMAT = 'yyyy-MM';

    const months = document.querySelectorAll('.react-datepicker__month-container');
    let lastShownMonth = parse(months[1]?.childNodes[1]?.ariaLabel?.split('  ')[1], DATE_FORMAT, new Date());

    // Hack for Firefox
    if (!lastShownMonth) {
      let ariaLabelString = months[1]?.childNodes[1]?.outerHTML;
      let extractedAriaLabel = ariaLabelString?.slice(ariaLabelString?.indexOf('aria-label') + 12);
      let trimmedAriaLabel = extractedAriaLabel?.slice(0, extractedAriaLabel?.indexOf('"'));
      lastShownMonth = parse(trimmedAriaLabel?.split('  ')[1], DATE_FORMAT, new Date());
    }

    return lastShownMonth;
  };

  const hideNextButtons = (nextButtons) => {
    nextButtons.forEach((button) => {
      button.style.display = 'none';
    });
  };

  const showNextButton = (previousButton, nextButton) => {
    previousButton?.addEventListener('click', () => {
      nextButton.style.display = 'block';
    });
  };

  useLayoutEffect(() => {
    const nextButtons = Array.from(document.querySelectorAll('.react-datepicker__navigation--next'));
    const previousButton = document.querySelector('.react-datepicker__navigation--previous');

    if (isBefore(startOfMonth(datePickerEndDate), add(getLastShownMonth(), { months: 2 })) && nextButtons.length > 0) {
      hideNextButtons(nextButtons);
    }

    nextButtons[0]?.addEventListener('click', (e) => {
      const lastShownMonth = getLastShownMonth();

      if (isBefore(startOfMonth(datePickerEndDate), add(lastShownMonth, { months: 2 }))) {
        e.target.style.display = 'none';
      }
      showNextButton(previousButton, nextButtons[0]);
    });

    showNextButton(previousButton, nextButtons[0]);
  }, [datePickerEndDate, type, filterState]);

  return (
    <FilterItem
      label={translate('holidayFinder.mainFilter.when')}
      placeholder={translate('holidayFinder.mainFilter.any')}
      eventName="when"
      value={label}
      className="dates-filter"
      icon="ri-calendar-line"
      onSave={handleSaveClick}
      onClear={handleClearClick}
      onOpen={handleOpen}
      onClose={handleClose}
      showClearButton={showClearButton}
      id="dates-filter"
      clean={isClean}
      resetOnClickOutside={onClickOutside}
      isOpen={isOpenPopup}
      setIsOpen={setIsOpenPopup}
    >
      <div className="main-filter__item-wrap dates-filter">
        <div className="dates-filter__type-wrap">
          <TypeSwitcher type={type} onChange={updateType} />

          {showDatesTutorial && <DatesTutorial />}

          <div className={`dates-filter__calendar ${type}`}>
            {type === 'month' && (
              <Months
                months={flexible.months}
                selectedMonths={months}
                weekendsOnly={weekendsOnly}
                onChange={onMonthsChange}
                monthAhead={monthAhead}
              />
            )}
            {type === 'specific' && (
              <DatePicker
                selected={startDate}
                onChange={onDateChange}
                startDate={startDate}
                endDate={endDate}
                minDate={addDays(new Date(), 1)}
                maxDate={endDateWithLastDay}
                monthsShown={window.innerWidth < 768 ? monthAhead : 2}
                disabledKeyboardNavigation
                selectsRange
                inline
                shouldCloseOnSelect={false}
                openToDate={window.innerWidth < 768 ? addDays(new Date(), 2) : undefined}
              />
            )}
            {type === 'range' && (
              <div className="dates-filter__calendar-range">
                {window.innerHeight > 720 && !abTesting.hideDateTutorial && (
                  <div className="calendar-label">{translate('holidayFinder.mainFilter.date_range_label')}</div>
                )}
                <DatePicker
                  selected={startDate}
                  onChange={onDateChange}
                  startDate={startDate}
                  endDate={endDate}
                  minDate={addDays(new Date(), 1)}
                  maxDate={endDateWithLastDay}
                  monthsShown={window.innerWidth < 768 ? monthAhead : 2}
                  disabledKeyboardNavigation
                  selectsRange
                  inline
                  shouldCloseOnSelect={false}
                  openToDate={window.innerWidth < 768 ? addDays(new Date(), 2) : undefined}
                />
              </div>
            )}

            {(type === 'range' || type === 'month') && (
              <div className="dates-filter-nights__wrap">
                <div className="dates-filter-nights__title__wrap">
                  <div className="dates-filter-nights__title">
                    {translate('holidayFinder.mainFilter.number_of_nights')}
                  </div>
                  <div className="dates-filter__weekends-only">
                    <Switch
                      value={weekendsOnly && !weekendsDisabled}
                      label={translate('holidayFinder.mainFilter.weekends_only')}
                      onChange={onChangeWeekendsOnly}
                      disable={weekendsDisabled}
                    />
                  </div>
                </div>
                <DateNights nights={nights} onChange={changeNights} daysInterval={daysInterval} />
              </div>
            )}
          </div>
        </div>
      </div>
    </FilterItem>
  );
}

export default React.memo(Dates);
